import { useState } from "react";
import emailjs from "@emailjs/browser";
import { useContactData } from "../../../lib/useMockData";
import ScrollReveal from "../../../components/feature/ScrollReveal";
import { useTranslation } from "react-i18next";

// ─── EmailJS 設定 ──────────────────────────────────────────────
// https://www.emailjs.com でアカウント作成後、以下を入力してください
const EJS_SERVICE_ID  = import.meta.env.VITE_EMAILJS_SERVICE_ID  ?? "YOUR_SERVICE_ID";
const EJS_ADMIN_TID   = import.meta.env.VITE_EMAILJS_ADMIN_TID   ?? "YOUR_ADMIN_TEMPLATE_ID";
const EJS_REPLY_TID   = import.meta.env.VITE_EMAILJS_REPLY_TID   ?? "YOUR_REPLY_TEMPLATE_ID";
const EJS_PUBLIC_KEY  = import.meta.env.VITE_EMAILJS_PUBLIC_KEY  ?? "YOUR_PUBLIC_KEY";
const ADMIN_EMAIL     = "ludoa.member2@gmail.com";
// ──────────────────────────────────────────────────────────────

export default function ContactForm() {
  const { formFields } = useContactData();
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";

  const [submitted, setSubmitted] = useState(false);
  const [sending,   setSending]   = useState(false);
  const [error,     setError]     = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const form     = e.currentTarget;
    const formData = new FormData(form);

    const name          = (formData.get("name")          as string).trim();
    const email         = (formData.get("email")         as string).trim();
    const phone         = (formData.get("phone")         as string).trim();
    const inquiryType   = (formData.get("inquiryType")   as string).trim();
    const preferredDate = (formData.get("preferredDate") as string).trim();
    const message       = (formData.get("message")       as string).trim();

    if (!name || !email || !message) {
      setError(isEn ? "Please fill in all required fields." : "必須項目をすべて入力してください。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(isEn ? "Please enter a valid email address." : "正しいメールアドレスを入力してください。");
      return;
    }

    setSending(true);
    try {
      const params = {
        admin_email:    ADMIN_EMAIL,
        user_name:      name,
        user_email:     email,
        user_phone:     phone    || "—",
        inquiry_type:   inquiryType   || "—",
        preferred_date: preferredDate || "—",
        message,
        reply_to:       email,
      };

      // 1) 管理者への通知メール (to: ludoa.member2@gmail.com)
      await emailjs.send(EJS_SERVICE_ID, EJS_ADMIN_TID, params, EJS_PUBLIC_KEY);

      // 2) 送信者への自動返信 (from: ludoa.member2@gmail.com, to: user_email)
      await emailjs.send(EJS_SERVICE_ID, EJS_REPLY_TID, params, EJS_PUBLIC_KEY);

      setSubmitted(true);
      form.reset();
    } catch (err) {
      console.error("EmailJS error:", err);
      setError(
        isEn
          ? "An error occurred. Please try again later."
          : "送信中にエラーが発生しました。時間をおいて再度お試しください。"
      );
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-16 md:py-24 bg-airdoa-white">
        <div className="px-4 md:px-8 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-airdoa-cream text-airdoa-gold mb-6">
              <i className="ri-check-line text-2xl" />
            </div>
            <h2 className="text-xl md:text-2xl font-serif font-semibold text-airdoa-navy mb-3">
              {isEn ? "Your inquiry has been received." : "お問い合わせを受け付けました"}
            </h2>
            <p className="text-sm text-airdoa-gray leading-relaxed">
              {isEn
                ? "Our staff will get back to you within 2 business days.\nPlease check your spam folder if you don't receive a reply."
                : "専任スタッフが内容を確認し、2営業日以内にご返信いたします。\nメールが届かない場合は、迷惑メールフォルダもご確認ください。"}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-airdoa-white">
      <div className="px-4 md:px-8 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <ScrollReveal direction="up">
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.name.label}<span className="text-red-400 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder={formFields.name.placeholder}
                  required
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.email.label}<span className="text-red-400 ml-1">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder={formFields.email.placeholder}
                  required
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.phone.label}
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder={formFields.phone.placeholder}
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.inquiryType.label}<span className="text-red-400 ml-1">*</span>
                </label>
                <select
                  name="inquiryType"
                  required
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors appearance-none"
                >
                  <option value="">{isEn ? "Please select" : "選択してください"}</option>
                  {formFields.inquiryType.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.preferredDate.label}
                </label>
                <input
                  type="text"
                  name="preferredDate"
                  placeholder={formFields.preferredDate.placeholder}
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.message.label}<span className="text-red-400 ml-1">*</span>
                </label>
                <textarea
                  name="message"
                  placeholder={formFields.message.placeholder}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors resize-none"
                />
                <p className="mt-1 text-xs text-airdoa-gray">
                  {isEn ? "Up to 500 characters" : "500文字以内でご記入ください"}
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-100 rounded-md text-sm text-red-500">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={sending}
                className="btn-primary w-full text-center disabled:opacity-60"
              >
                {sending
                  ? (isEn ? "Sending…" : "送信中…")
                  : (isEn ? "Send" : "送信する")}
              </button>
            </form>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
